import { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { AppShell } from '../components/AppShell';
import {
  TICK_LISTS, resolveDailyTemplate,
  flattenItems,
  type TickKind,
} from '../data/tickListTemplates';
import {
  getSite,
  getLatestTickRun,
  type Site,
  type TickListRun,
} from '../lib/db';
import { getSignedUrl } from '../lib/storage';
import { generateTickListRunPdf } from '../lib/pdf';
import { useUserOrg } from '../lib/org';
import { LoadingIndicator } from '../components/LoadingIndicator';

function fmtDateTime(iso: string): string {
  return new Date(iso).toLocaleString('en-GB', {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function TickListRunView() {
  const { siteId, kind } = useParams<{ siteId: string; kind: string }>();
  const nav = useNavigate();

  const [site, setSite] = useState<Site | null>(null);
  const tpl = (() => {
    if (!kind || !(kind in TICK_LISTS)) return null;
    if (kind === 'daily') return resolveDailyTemplate(site?.project_type);
    return TICK_LISTS[kind as TickKind];
  })();

  const [run, setRun] = useState<TickListRun | null>(null);
  const [photoUrls, setPhotoUrls] = useState<Record<string, string[]>>({});
  const { org } = useUserOrg();
  const [exporting, setExporting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!siteId || !tpl) return;
    (async () => {
      try {
        const [s, r] = await Promise.all([
          getSite(siteId),
          getLatestTickRun(siteId, tpl.kind),
        ]);
        setSite(s);
        setRun(r);
        if (r) {
          // Resolve signed URLs for any photos
          const out: Record<string, string[]> = {};
          for (const [key, resp] of Object.entries(r.responses)) {
            if (resp?.photo_paths?.length) {
              const urls = await Promise.all(
                resp.photo_paths.map((p) => getSignedUrl('accident-photos', p, 3600))
              );
              out[key] = urls;
            }
          }
          setPhotoUrls(out);
        }
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [siteId, kind, tpl]);

  async function exportPdf() {
    if (!org || !site || !run) return;
    setExporting(true);
    try {
      const blob = await generateTickListRunPdf({
        orgName: org.org_name,
        orgLogoPath: org.logo_path,
        site,
        run,
      });
      const url = URL.createObjectURL(blob);
      const dateStr = new Date(run.performed_at).toISOString().slice(0, 10);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${run.kind}-${site.name.replace(/\s+/g, '-')}-${dateStr}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setExporting(false);
    }
  }

  if (loading) {
    return (
      <AppShell title="Check" back={`/sites/${siteId ?? ''}/checks`}>
        <LoadingIndicator />
      </AppShell>
    );
  }

  if (!site || !tpl) {
    return (
      <AppShell title="Check" back={`/sites/${siteId ?? ''}/checks`}>
        <div className="banner banner-error">{error ?? 'Not found.'}</div>
      </AppShell>
    );
  }

  if (!run) {
    return (
      <AppShell title={tpl.title} back={`/sites/${site.id}/checks`}>
        <div className="card" style={{ textAlign: 'center' }}>
          <p style={{ margin: 0, color: 'var(--ink-soft)' }}>
            No run yet on this site.
          </p>
        </div>
        <div className="btn-stack">
          <Link to={`/sites/${site.id}/checks/${tpl.kind}/run`} className="btn btn-primary" style={{ display: 'block', textAlign: 'center', textDecoration: 'none' }}>
            Run now
          </Link>
        </div>
      </AppShell>
    );
  }

  const flat = flattenItems(tpl);

  return (
    <AppShell title={tpl.title} back={`/sites/${site.id}/checks`}>
      <div className="card" style={{ marginBottom: 16 }}>
        <p style={{ margin: 0, fontWeight: 600 }}>
          {fmtDateTime(run.performed_at)}
        </p>
        <p style={{ margin: '6px 0 0', color: 'var(--ink-soft)', fontSize: 14 }}>
          By {run.performed_by_name ?? 'Unknown'}
        </p>
        {run.notes && (
          <p style={{ margin: '10px 0 0', fontSize: 14 }}>
            <strong>Notes:</strong> {run.notes}
          </p>
        )}
      </div>

      <button
        type="button"
        className="btn btn-primary"
        onClick={() => nav(`/sites/${site.id}/checks/${tpl.kind}/run`)}
        style={{ marginBottom: 8 }}
      >
        Run again
      </button>
      <button
        type="button"
        className="btn btn-ghost"
        onClick={exportPdf}
        disabled={exporting}
        style={{ marginBottom: 16 }}
      >
        {exporting ? 'Exporting…' : 'Export PDF'}
      </button>

      {tpl.sections.map((sec, si) => (
        <div key={si} style={{ marginTop: 12 }}>
          {sec.name && <h3 className="check-section-h">{sec.name}</h3>}
          <ol className="tick-list">
            {sec.items.map((item, ii) => {
              const key = `s${si}_i${ii}`;
              const resp = run.responses[key];
              const state = resp?.state;
              const stateLabel =
                state === 'pass' ? 'OK' :
                state === 'fail' ? 'Issue' :
                state === 'na' ? 'N/A' : '—';
              const stateClass =
                state === 'pass' ? 'badge-success' :
                state === 'fail' ? 'badge-danger' :
                state === 'na' ? 'badge-neutral' : 'badge-neutral';

              return (
                <li key={key} className={`tick-row ${state === 'fail' ? 'has-issue' : ''}`}>
                  <div className="tick-row-main">
                    <span className="tick-num">{flat.findIndex((f) => f.key === key) + 1}</span>
                    <div className="tick-text">
                      <strong>{item.text}</strong>
                      {item.desc && <p className="tick-desc">{item.desc}</p>}
                      <p style={{ margin: '6px 0 0' }}>
                        <span className={`badge ${stateClass}`}>{stateLabel}</span>
                      </p>
                      {resp?.notes && (
                        <p style={{ margin: '6px 0 0', fontSize: 14 }}>
                          <em>{resp.notes}</em>
                        </p>
                      )}
                      {photoUrls[key]?.length ? (
                        <div className="photo-grid" style={{ marginTop: 8 }}>
                          {photoUrls[key].map((url, i) => (
                            <a key={i} href={url} target="_blank" rel="noreferrer" className="photo-thumb static">
                              <img src={url} alt={`Issue photo ${i + 1}`} />
                            </a>
                          ))}
                        </div>
                      ) : null}
                    </div>
                  </div>
                </li>
              );
            })}
          </ol>
        </div>
      ))}
    </AppShell>
  );
}
